import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Badge,
  Popover,
  PopoverBody,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';
import { isSearchPageRoute } from '../../helpers/PageSections';

import SidebarFilters from './Filters';

import {
  setSidebarActiveTab,
  setSidebarOpen,
  setSecondSidebarOpen,
} from '../../actions/App';
import { fetchExternalFilters } from '../../actions/Search';

import { isCollectionsPage } from '../../helpers/Collections';
import { filtersCount } from '../../helpers/Files';

const SidebarFiltersSwitch = ({
  sidebarActiveTab,
  setActiveTab,
  setOpen,
  setSecondSidebarOpenAction,
  filters,
  collectionFilters,
  search,
  pageId,
  view,
  fetchPageFilters,
  location,
}) => {
  const intl = useIntl();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const filtersSwitchRef = useRef(null);

  const collectionUuid = location.pathname.match(/^\/(?:pages|content)\/[^/]+\/collection\/([^/]+)\/?$/)?.[1] || null;
  const showFiltersPopover = (/^\/pages\/[^/]+$/.test(location.pathname) || collectionUuid) && !isSearchPageRoute();

  useEffect(() => {
    if (showFiltersPopover && pageId) {
      fetchPageFilters(pageId, collectionUuid);
    }
  }, [showFiltersPopover, pageId, collectionUuid, fetchPageFilters, location]);

  useEffect(() => {
    setPopoverOpen(false);
  }, [location]);

  let selectedFilters = isCollectionsPage()
    ? filtersCount(collectionFilters)
    : filtersCount(filters);

  selectedFilters += filtersCount([search || null]);

  const label = sidebarActiveTab === '2'
    ? intl.formatMessage({ defaultMessage: 'Hide filters' })
    : intl.formatMessage({ defaultMessage: 'Show filters' });

  return (
    <span className={classnames('filters-switch', { active: sidebarActiveTab === '2' || popoverOpen })}>
      <a
        id="filters-switch"
        ref={filtersSwitchRef}
        href="#filters"
        onClick={(e) => {
          e.preventDefault();

          if (showFiltersPopover) {
            setPopoverOpen(open => !open);
          } else if (sidebarActiveTab === '2') {
            setActiveTab('1');
            setOpen(false);
          } else {
            setActiveTab('2');
            setOpen(true);
            setSecondSidebarOpenAction(true);
          }
        }}
        aria-label={label}
      >
        <i className="fal fa-sliders-h" />
      </a>
      {selectedFilters > 0 && (
        <Badge color="primary">
          {selectedFilters}
        </Badge>
      )}
      {!showFiltersPopover && (
        <UncontrolledTooltip fade={false} target="filters-switch">
          {label}
        </UncontrolledTooltip>
      )}
      {showFiltersPopover && (
        <Popover
          placement="bottom-start"
          positionFixed
          isOpen={popoverOpen}
          target={() => filtersSwitchRef.current}
          toggle={() => setPopoverOpen(open => !open)}
          trigger="legacy"
          container={() => (
            filtersSwitchRef.current?.closest('.landing-toolbar')
              ?.querySelector('.landing-search-filters-placeholder') || document.body
          )}
          className="filters-popover"
          flip={false}
          modifiers={{ preventOverflow: { enabled: false }, hide: { enabled: false } }}
        >
          <PopoverBody>
            <SidebarFilters
              type={null}
              searchPathSuffix="/search"
              externalSearch
              inPopover={true}
              view={view}
            />
          </PopoverBody>
        </Popover>
      )}
    </span>
  );
};

const mapStateToProps = state => ({
  sidebarActiveTab: state.App.sidebarActiveTab,
  filters: state.Files.filters,
  collectionFilters: state.Collections.filters,
  search: state.Search.search,
  searchFields: state.Search.search_fields,
});

const mapDispatchToProps = dispatch => ({
  setActiveTab: (tab) => {
    dispatch(setSidebarActiveTab(tab));
  },
  setOpen: (open) => {
    dispatch(setSidebarOpen(open));
  },
  setSecondSidebarOpenAction: (open) => {
    dispatch(setSecondSidebarOpen(open));
  },
  fetchPageFilters: (pageId, collectionUuid) => {
    dispatch(fetchExternalFilters('custom-page', pageId, { collectionUuid }));
  },
});

SidebarFiltersSwitch.propTypes = {
  location: PropTypes.object.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  setOpen: PropTypes.func.isRequired,
  setSecondSidebarOpenAction: PropTypes.func.isRequired,
  sidebarActiveTab: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  search: PropTypes.string,
  collectionFilters: PropTypes.object.isRequired,
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  view: PropTypes.string,
  fetchPageFilters: PropTypes.func.isRequired,
};

SidebarFiltersSwitch.defaultProps = {
  search: null,
  pageId: null,
  view: null,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SidebarFiltersSwitch));
